import { Flex, FlexProps, Theme, useTheme } from '@chakra-ui/react';
import { MessageType, MessageActions } from 'types/chat.types';
import {
  getAvatarColorsArray,
  getRandomColor,
} from 'app/components/Core/ChakraAvatar/utils';
import { getAvatarImage } from 'app/utils/utils';
import { ChakraAvatar } from 'app/components/Core';
import { MessageProvider } from 'app/components/Chat/context';
import { DeletedMessage } from './DeletedMessage/DeletedMessage';
import { UserAndTimeBlock } from './UserAndTimeBlock/UserAndTimeBlock';
import { RetryMessageBlock } from './RetryMessageBlock/RetryMessageBlock';
import { SimpleMessage } from './SimpleMessage/SimpleMessage';

export interface MessageProps extends FlexProps {
  message: MessageType;
  isCurrentUser?: boolean;
  messageActions: MessageActions;
}

export const Message = ({
  message,
  isCurrentUser,
  messageActions,
  onContextMenu,
  ...rest
}: MessageProps) => {
  const { guid, deleted, needRetry, user, created } = message;
  const theme = useTheme<Theme>();
  const randomColor = getRandomColor(
    getAvatarColorsArray(theme),
    user?.initials
  );

  if (message.guid === 'ignore') {
    return null;
  }

  return (
    <MessageProvider value={{ messageActions, message }}>
      <Flex
        key={guid}
        justifyContent={isCurrentUser ? 'flex-end' : 'flex-start'}
        {...rest}
      >
        <Flex
          flexDirection="column"
          alignItems={isCurrentUser ? 'flex-end' : 'flex-start'}
        >
          <Flex alignItems="flex-end">
            {!isCurrentUser && (
              <ChakraAvatar
                size="sm"
                mr={2}
                initials={user?.initials}
                src={getAvatarImage(user?.profileImages)}
                colors={
                  user?.avatarColors || {
                    background: randomColor[200],
                    text: randomColor[500],
                  }
                }
              />
            )}
            {deleted ? (
              <DeletedMessage
                key={guid}
                isCurrentUser={isCurrentUser}
                data-testid={`messenger-${
                  isCurrentUser ? 'my' : 'others'
                }-deleted-message`}
              />
            ) : (
              <SimpleMessage
                isCurrentUser={isCurrentUser}
                data-testid={`messenger-${
                  isCurrentUser ? `${needRetry ? 'my-retry' : 'my'}` : 'others'
                }-message`}
                onContextMenu={onContextMenu}
              />
            )}
          </Flex>

          <Flex ml={isCurrentUser ? 0 : 10}>
            {needRetry ? (
              <RetryMessageBlock />
            ) : (
              <UserAndTimeBlock
                isCurrentUser={isCurrentUser}
                created={created}
                displayName={user?.displayName}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </MessageProvider>
  );
};
